import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { useAuthMe, useMonitoring } from '../../hooks/queries';
import { canAccessMonitoring } from '../../utils/support-user';
import {
  applySort,
  DEFAULT_SORT_DIRECTION,
  type MonitoringSortColumn,
  type MonitoringSortState,
} from '../../utils/sort-monitoring-users';
import { UserActivityDetailPanel } from './UserActivityDetailPanel';


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

function statusDot(s: string) {
  if (s === 'ok' || s === 'fresh') { return '🟢'; }
  if (s === 'warning') { return '🟡'; }
  if (s === 'error' || s === 'stale') { return '🔴'; }
  return '🔵';
}

function fmtMs(ms: number) {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function fmtAge(mins: number | null) {
  if (mins === null) { return '—'; }
  if (mins < 0) { return 'just now'; }
  if (mins < 60) { return `${mins}m ago`; }
  if (mins < 1440) { return `${Math.floor(mins / 60)}h ${mins % 60}m ago`; }
  return `${Math.floor(mins / 1440)}d ago`;
}

function KPICard({ label, value, sub, status }: { label: string; value: string | number; sub?: string; status?: string }) {
  const color = status === 'ok' || status === 'fresh' ? '#16a34a' : status === 'warning' ? '#d97706' : status === 'error' || status === 'stale' ? '#dc2626' : '#2563eb';
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.875rem 1rem', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>{sub}</div>}
      {status && <div style={{ fontSize: '0.72rem', marginTop: '0.4rem' }}>{statusDot(status)} {status.charAt(0).toUpperCase() + status.slice(1)}</div>}
    </div>
  );
}

export function MonitoringDashboard() {
  const [hours, setHours] = useState(24);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [sortState, setSortState] = useState<MonitoringSortState>({
    column: 'lastSeen',
    direction: 'asc',
  });
  const isMobile = useIsMobile();
  const { data: authMeResponse } = useAuthMe();
  const persona = authMeResponse?.data;
  const isAdmin = canAccessMonitoring(persona);

  const { data, isLoading: loading, error: queryError, refetch } = useMonitoring(
    hours,
    isAdmin,
  );
  const error = queryError ? ((queryError as any)?.response?.data?.message ?? 'Failed to load monitoring data') : null;

  useEffect(() => { if (data) { setLastFetch(new Date()); } }, [data]);

  const sortedUsers = useMemo(() => {
    if (!data?.users?.list) {
      return [];
    }
    return applySort(data.users.list, sortState);
  }, [data?.users?.list, sortState]);

  const handleSort = (column: MonitoringSortColumn) => {
    if (loading) {
      return;
    }
    setSortState((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: DEFAULT_SORT_DIRECTION };
    });
  };

  const getSortIndicator = (column: MonitoringSortColumn): string | null =>
    sortState.column !== column ? null : sortState.direction === 'asc' ? '↑' : '↓';

  const getAriaSort = (column: MonitoringSortColumn): 'ascending' | 'descending' | 'none' =>
    sortState.column !== column
      ? 'none'
      : sortState.direction === 'asc'
        ? 'ascending'
        : 'descending';

  const card: React.CSSProperties = {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: isMobile ? '1rem' : '1.25rem', marginBottom: '1rem',
  };
  const sectionTitle: React.CSSProperties = {
    fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.75rem',
  };
  const thStyle = (left = false): React.CSSProperties => ({
    textAlign: left ? 'left' : 'right', padding: '0.4rem 0.5rem',
    color: '#64748b', fontWeight: 600, fontSize: '0.68rem',
    textTransform: 'uppercase', whiteSpace: 'nowrap',
  });

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '1rem 0.75rem' : '1.5rem 1rem', fontFamily: 'system-ui, sans-serif', color: '#0f172a' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.35rem', fontWeight: 800 }}>Production Monitoring</h1>
          {lastFetch && <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 2 }}>Refreshed {lastFetch.toLocaleTimeString()} · auto-refreshes every 60s</div>}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {(['6', '24', '48'] as const).map(h => (
            <button key={h} onClick={() => setHours(Number(h))}
              style={{ padding: '0.3rem 0.65rem', borderRadius: 6, border: '1px solid #e2e8f0', background: hours === Number(h) ? '#2563eb' : '#fff', color: hours === Number(h) ? '#fff' : '#374151', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
              {h}h
            </button>
          ))}
          <button onClick={() => refetch()} style={{ padding: '0.3rem 0.65rem', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}>
            ↻
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.75rem 1rem', color: '#dc2626', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}
      {loading && !data && <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>Loading monitoring data…</div>}

      {data && (
        <>
          {/* System Health — 2×2 on mobile, 4 across on desktop */}
          <div style={{ ...card }}>
            <div style={sectionTitle}>System Health</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '0.6rem' }}>
              <KPICard label="API Gateway" value={data.apiGateway.requests.toLocaleString()} sub={`${data.apiGateway.errorRate}% errors · avg ${data.apiGateway.avgLatencyMs}ms`} status={data.apiGateway.status} />
              <KPICard label="Lambdas" value={`${data.lambdas.okRate}% OK`} sub={`${data.lambdas.totalErrors} errors · p95 ${fmtMs(data.lambdas.p95Ms)}`} status={data.lambdas.totalErrors > 0 ? 'warning' : 'ok'} />
              <KPICard label="Warehouse" value={data.warehouse.status === 'fresh' ? 'Fresh' : 'Stale'} sub={`Synced ${fmtAge(data.warehouse.staleMins)}`} status={data.warehouse.status} />
              <KPICard label="AMBR Orders" value={data.warehouse.ambrTotalOrders ?? '—'} sub={`${data.warehouse.ambrRefunded ?? 0} refunded`} status="ok" />
            </div>
          </div>

          {/* Order Export Sync */}
          <div style={{ ...card }}>
            <div style={sectionTitle}>Order Export Sync</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '0.6rem' }}>
              <KPICard
                label="Export Status"
                value={
                  !data.orderExport?.status || data.orderExport.status === 'idle'
                    ? 'Idle'
                    : data.orderExport.status === 'stale'
                      ? 'Running (stale)'
                      : data.orderExport.status.charAt(0).toUpperCase() + data.orderExport.status.slice(1)
                }
                sub={
                  data.orderExport?.staleRunning
                    ? 'No progress in 15+ min — job may be stuck'
                    : data.orderExport?.jobId
                      ? `Job ${data.orderExport.jobId.slice(0, 8)}…`
                      : 'No active export'
                }
                status={
                  data.orderExport?.status === 'completed' ? 'ok'
                    : data.orderExport?.status === 'stale' ? 'error'
                      : data.orderExport?.status === 'running' ? 'warning'
                        : data.orderExport?.status === 'failed' ? 'error'
                          : 'ok'
                }
              />
              <KPICard
                label="Orders Synced"
                value={data.orderExport?.ordersSynced?.toLocaleString() ?? '—'}
                sub={data.orderExport?.batchesWritten !== undefined ? `${data.orderExport.batchesWritten} batches` : undefined}
                status="ok"
              />
              <KPICard
                label="Date Range"
                value={data.orderExport?.startDate && data.orderExport?.endDate
                  ? `${data.orderExport.startDate.slice(5)} → ${data.orderExport.endDate.slice(5)}`
                  : '—'}
                sub={data.orderExport?.updatedAt ? `Updated ${fmtAge(Math.floor((Date.now() - new Date(data.orderExport.updatedAt).getTime()) / 60000))}` : undefined}
                status="ok"
              />
              <KPICard
                label="Staging Bucket"
                value="bci-order-export"
                sub="Separate from warehouse UI bucket"
                status="ok"
              />
            </div>
            {data.orderExport?.errorMessage && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#dc2626' }}>
                {data.orderExport.errorMessage}
              </div>
            )}
          </div>

          {/* User Activity */}
          <div style={{ ...card }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.25rem' }}>
              <div style={sectionTitle}>User Activity</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                <strong style={{ color: '#0f172a' }}>{data.users.uniqueToday}</strong> unique users in last {hours}h
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              {/* User table — horizontally scrollable */}
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: isMobile ? 420 : 'auto' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                      <th
                        style={{ ...thStyle(true), cursor: 'pointer' }}
                        onClick={() => handleSort('user')}
                        aria-sort={getAriaSort('user')}
                        data-testid="monitoring-sort-user"
                      >
                        User {getSortIndicator('user')}
                      </th>
                      <th
                        style={{ ...thStyle(), cursor: 'pointer' }}
                        onClick={() => handleSort('requests')}
                        aria-sort={getAriaSort('requests')}
                        data-testid="monitoring-sort-requests"
                      >
                        Reqs {getSortIndicator('requests')}
                      </th>
                      <th
                        style={{ ...thStyle(), cursor: 'pointer' }}
                        onClick={() => handleSort('lastSeen')}
                        aria-sort={getAriaSort('lastSeen')}
                        data-testid="monitoring-sort-lastSeen"
                      >
                        Last Seen {getSortIndicator('lastSeen')}
                      </th>
                      <th style={thStyle()} title="Desktop">💻</th>
                      <th style={thStyle()} title="Mobile">📱</th>
                      <th style={thStyle()} title="Tablet">⬜</th>
                      <th style={{ ...thStyle(), textAlign: 'center' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: '1rem 0.5rem', color: '#94a3b8', textAlign: 'center', fontSize: '0.8rem' }}>No user activity recorded yet.</td></tr>
                    )}
                    {sortedUsers.map((u) => {
                      const clickable = isAdmin && u.requests > 0;
                      return (
                      <tr
                        key={u.email}
                        data-testid={`monitoring-user-row-${u.email}`}
                        onClick={clickable ? () => setSelectedEmail(u.email) : undefined}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onKeyDown={clickable ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedEmail(u.email);
                          }
                        } : undefined}
                        style={{
                          borderBottom: '1px solid #f8fafc',
                          cursor: clickable ? 'pointer' : 'default',
                        }}
                      >
                        <td style={{ padding: '0.45rem 0.5rem', maxWidth: 150 }}>
                          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{u.name || u.email}</div>
                          {u.name && <div style={{ fontSize: '0.68rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>{u.requests}</td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: '#64748b', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{fmtAge(u.minsAgo)}</td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>
                          {u.desktop > 0 ? <span style={{ color: '#374151', fontWeight: 600 }}>{u.desktop}</span> : <span style={{ color: '#e2e8f0' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>
                          {u.mobile > 0 ? <span style={{ color: '#2563eb', fontWeight: 600 }}>{u.mobile}</span> : <span style={{ color: '#e2e8f0' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>
                          {u.tablet > 0 ? <span style={{ color: '#7c3aed', fontWeight: 600 }}>{u.tablet}</span> : <span style={{ color: '#e2e8f0' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center' }}>{u.active ? '🟢' : u.minsAgo !== null && u.minsAgo < 1440 ? '🟡' : '🔴'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* DAU chart */}
              <div>
                <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Daily Active Users — last 14 days</div>
                {data.dauChart.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', paddingTop: '0.5rem' }}>No data yet.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={isMobile ? 140 : 160}>
                    <BarChart data={data.dauChart} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} allowDecimals={false} />
                      <Tooltip formatter={(v: any) => [v, 'Users']} labelFormatter={l => `Date: ${l}`} contentStyle={{ fontSize: '0.75rem' }} />
                      <Bar dataKey="dau" fill="#2563eb" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Request Volume + Endpoints — side by side on desktop, stacked on mobile */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ ...card, marginBottom: 0 }}>
              <div style={sectionTitle}>Request Volume — prod (last {hours}h)</div>
              {data.hourlyVolume.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>No CloudWatch data available.</div>
              ) : (
                <ResponsiveContainer width="100%" height={isMobile ? 150 : 180}>
                  <LineChart data={data.hourlyVolume} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#94a3b8' }} interval={isMobile ? 5 : 3} />
                    <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} />
                    <Tooltip contentStyle={{ fontSize: '0.75rem' }} />
                    <Line type="monotone" dataKey="requests" stroke="#2563eb" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={{ ...card, marginBottom: 0 }}>
              <div style={sectionTitle}>Requests by Endpoint</div>
              {data.endpoints.breakdown.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>No data yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                  {data.endpoints.breakdown.map((e, i) => {
                    const pct = data.endpoints.total > 0 ? (e.requests / data.endpoints.total) * 100 : 0;
                    const short = e.path.replace('/api/', '');
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
                          <span style={{ color: '#374151', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{short}</span>
                          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>{e.requests.toLocaleString()}</span>
                        </div>
                        <div style={{ height: 4, background: '#f1f5f9', borderRadius: 999 }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: i === 0 ? '#2563eb' : i === 1 ? '#3b82f6' : '#93c5fd', borderRadius: 999 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Lambda Health — horizontally scrollable on mobile */}
          <div style={{ ...card }}>
            <div style={sectionTitle}>Lambda Health — Prod Functions</div>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 480 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                    {['Function', 'Invocations', 'Errors', 'Error %', 'p95', 'Avg', 'Status'].map(h => (
                      <th key={h} style={thStyle(h === 'Function')}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.lambdas.functions.map((fn, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '0.45rem 0.5rem', fontWeight: 500, fontFamily: 'monospace', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{fn.name}</td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>{fn.invocations.toLocaleString()}</td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: fn.errors > 0 ? '#dc2626' : '#16a34a', fontWeight: fn.errors > 0 ? 700 : 400 }}>{fn.errors}</td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: fn.errorRate > 5 ? '#dc2626' : fn.errorRate > 0 ? '#d97706' : '#64748b' }}>{fn.invocations > 0 ? `${fn.errorRate}%` : '—'}</td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: fn.p95 > 10000 ? '#dc2626' : fn.p95 > 5000 ? '#d97706' : '#374151' }}>{fn.p95 > 0 ? fmtMs(fn.p95) : '—'}</td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: '#64748b' }}>{fn.avg > 0 ? fmtMs(fn.avg) : '—'}</td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>{fn.invocations === 0 ? '🔵' : statusDot(fn.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Errors */}
          <div style={{ ...card, marginBottom: 0 }}>
            <div style={sectionTitle}>Recent Errors</div>
            {data.recentErrors.length === 0 ? (
              <div style={{ color: '#16a34a', fontSize: '0.82rem' }}>🟢 No errors in the last {hours}h</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {data.recentErrors.map((e, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.6rem', padding: '0.5rem 0.75rem', background: '#fff5f5', borderRadius: 6, fontSize: '0.75rem', borderLeft: '3px solid #dc2626', alignItems: 'flex-start' }}>
                    <span style={{ color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>{e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : '—'}</span>
                    <span style={{ color: '#374151', wordBreak: 'break-word', overflow: 'hidden' }}>{e.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#cbd5e1', marginTop: '0.75rem' }}>
            Generated {new Date(data.generatedAt).toLocaleString()} · {hours}h window
          </div>
        </>
      )}

      {selectedEmail && (
        <UserActivityDetailPanel
          email={selectedEmail}
          hours={hours}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </div>
  );
}
